import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Button } from 'primeng/button';
import {
  FormControlComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  SelectItem,
  UrlValidator,
} from '@web/shared/ui';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { EditorModule } from 'primeng/editor';
import {
  AdminMultimediaImageUpload,
  MultimediaChooseOrUploadControlComponent,
} from '@web/shared/module/multimedia';
import { executeOrNull, isNil, Nillable, valueOrDefault } from '@web/utils';
import { AdminBrandDetailsFormSocialMediaCollection } from '../../models/admin-brand-details-form-social-media-collection';
import {
  AdminBrandDetailsForm,
  AdminBrandDetailsFormGroup,
} from '../../models/admin-brand-details-form';
import { AdminBrandSocialMediaComponent } from '../../containers/admin-brand-social-media/admin-brand-social-media.component';
import {
  SummernoteComponent,
  SummernoteImageUploadFn,
} from '@web/shared/external';
import { AdminBrandStatus } from '@web/shared/module/brand';
import { AdminBrandStatusProviderService } from '../../providers/admin-brand-status-provider.service';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Select } from 'primeng/select';

@Component({
  selector: 'app-admin-brand-details-form',
  imports: [
    AdminBrandSocialMediaComponent,
    Button,
    CommonModule,
    EditorModule,
    FormControlComponent,
    InputTextModule,
    MultimediaChooseOrUploadControlComponent,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
    SummernoteComponent,
  ],
  templateUrl: './admin-brand-details-form.component.html',
  styleUrl: './admin-brand-details-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminBrandDetailsFormComponent implements OnInit {
  private readonly builder = inject(FormBuilder);
  private readonly statusProvider = inject(AdminBrandStatusProviderService);

  public readonly data = input<Nillable<AdminBrandDetailsForm>>();
  public readonly uploadFn = input.required<SummernoteImageUploadFn>();
  public readonly isEditContext = input<boolean>(false);
  public readonly submitClickEvent = output<AdminBrandDetailsForm>();

  protected readonly form = this.buildForm();
  /* prettier-ignore */
  protected readonly statusSelectItems$ = this.statusSelectItemsSource();
  protected readonly valueOrDefault = valueOrDefault;
  private readonly destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.listenToStatusValueChanges();

    this.fillByData();
  }

  public handleSubmitClick(): void {
    this.submitClickEvent.emit(this.toData());
  }

  private listenToStatusValueChanges(): void {
    this.form.controls.status.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        if (this.isPrivateOrPublic(status)) {
          this.enableDescriptionValidation();
          this.enableIllustrationValidation();
          this.enableLogoValidation();

          return;
        }

        this.disableDescriptionValidation();
        this.disableIllustrationValidation();
        this.disableLogoValidation();
      });
  }

  private enableIllustrationValidation(): void {
    this.form.controls.illustration.setValidators([Validators.required]);
    this.form.controls.illustration.updateValueAndValidity();
  }

  private disableIllustrationValidation(): void {
    this.form.controls.illustration.clearValidators();
    this.form.controls.illustration.updateValueAndValidity();
  }

  private enableLogoValidation(): void {
    this.form.controls.logo.setValidators([Validators.required]);
    this.form.controls.logo.updateValueAndValidity();
  }

  private disableLogoValidation(): void {
    this.form.controls.logo.clearValidators();
    this.form.controls.logo.updateValueAndValidity();
  }

  private enableDescriptionValidation(): void {
    this.form.controls.description.setValidators([Validators.required]);
    this.form.controls.description.updateValueAndValidity();
  }

  private disableDescriptionValidation(): void {
    this.form.controls.description.clearValidators();
    this.form.controls.description.updateValueAndValidity();
  }

  private isPrivateOrPublic(status: Nillable<AdminBrandStatus>): boolean {
    if (isNil(status)) {
      return false;
    }

    /* prettier-ignore */
    return [
      AdminBrandStatus.Private,
      AdminBrandStatus.Public,
    ].includes(status);
  }

  private toData(): AdminBrandDetailsForm {
    const data = this.form.getRawValue();

    return new AdminBrandDetailsForm({
      name: data.name,
      description: data.description,
      logoId: data.logo?.id,
      illustrationId: data.illustration?.id,
      status: data.status!,
      homeUrl: data.homeUrl,
      socialMedia: data.socials,
      slug: data.slug,
    });
  }

  private buildForm(): AdminBrandDetailsFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      description: this.builder.control<Nillable<string>>(null, ),
      homeUrl: this.builder.control<Nillable<string>>(null, [UrlValidator.url]),
      illustration: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null),
      logo: this.builder.control<Nillable<AdminMultimediaImageUpload>>(null),
      name: this.builder.nonNullable.control('', [Validators.required]),
      slug: this.builder.control<Nillable<string>>(null),
      socials: this.builder.control<Nillable<AdminBrandDetailsFormSocialMediaCollection>>(null),
      status: this.builder.control<Nillable<AdminBrandStatus>>(null, [Validators.required]),
    });
  }

  private fillByData(): void {
    const data = this.data();

    /* prettier-ignore */
    this.form.patchValue({
      name: data?.name,
      description: data?.description,
      logo: executeOrNull(data?.logoId, (id) => new AdminMultimediaImageUpload({id})),
      illustration: executeOrNull(data?.illustrationId, (id) => new AdminMultimediaImageUpload({id})),
      homeUrl: data?.homeUrl,
      slug: data?.slug,
      socials: data?.socialMedia,
      status: data?.status,
    });
  }

  /* prettier-ignore */
  private statusSelectItemsSource(): Observable<SelectItem<AdminBrandStatus>[]> {
    return this.statusProvider.selectItems();
  }
}
